<template>
  <v-list density="compact" class="quick-reminder-menu" min-width="230">
    <v-list-item
      v-for="opt in presetOptions"
      :key="opt.key"
      @click="choosePreset(opt)"
    >
      <template v-slot:prepend>
        <v-icon :color="opt.color" size="20">{{ opt.icon }}</v-icon>
      </template>
      <v-list-item-title>{{ opt.label }}</v-list-item-title>
    </v-list-item>

    <v-list-item v-if="!showCustom" @click="showCustom = true">
      <template v-slot:prepend>
        <v-icon color="grey-darken-1" size="20">mdi-calendar-clock</v-icon>
      </template>
      <v-list-item-title>Custom</v-list-item-title>
    </v-list-item>

    <v-list-item v-else>
      <div class="d-flex align-center" style="gap: 6px; width: 100%;">
        <input
          type="datetime-local"
          v-model="customDate"
          :min="minReminderTime"
          style="width: 170px;"
        />
        <v-btn size="x-small" color="primary" variant="flat" @click="chooseCustom">
          Set
        </v-btn>
      </div>
    </v-list-item>

    <template v-if="hasReminder">
      <v-divider></v-divider>
      <v-list-item @click="$emit('remove')">
        <template v-slot:prepend>
          <v-icon color="red" size="20">mdi-bell-off-outline</v-icon>
        </template>
        <v-list-item-title class="text-red">Remove Reminder</v-list-item-title>
      </v-list-item>
    </template>
  </v-list>
</template>

<script>
import { DateFormats } from "../../constants/formats.js";

// Shared 5-option reminder picker: 1 hour / later today (4 PM) / tomorrow
// morning (8 AM) / custom (expands in place) / remove (only when a reminder
// already exists). Deliberately renders content only, no v-menu/activator or
// v-dialog wrapper of its own, so it can be dropped into three different
// containers unchanged: a v-menu off FDMessage.vue's inline quick-reminder
// icon, ReminderPanel.vue's existing v-dialog body, and a v-menu off the
// "Unattended" tag icon when a message has a reminder due.
export default {
  name: "QuickReminderMenu",
  props: {
    // true if a reminder already exists for the context this menu is acting
    // on (self or, via a targeted convo_reminder_id, someone else's) — gates
    // the "Remove Reminder" option, mirroring ReminderPanel's hasReminder.
    hasReminder: { type: Boolean, default: false },
    // existing reminder's date, to prefill Custom when editing one already set
    existingDate: { type: String, default: null },
  },
  emits: ["select", "remove"],
  data() {
    return {
      showCustom: false,
      customDate: this.existingDate
        ? this.dayjs(this.existingDate).format(DateFormats.DATE_TIME_FORMAT_24H)
        : this.dayjs().add(1, "minute").format(DateFormats.DATE_TIME_FORMAT_24H),
    };
  },
  computed: {
    minReminderTime() {
      return this.dayjs().add(1, "minute").format(DateFormats.DATE_TIME_FORMAT_24H);
    },
    presetOptions() {
      return [
        {
          key: "1hour",
          label: "1 Hour Later",
          icon: "mdi-clock-outline",
          color: "primary",
          date: this.dayjs().add(1, "hour"),
        },
        {
          key: "today4pm",
          label: "Later Today (4 PM)",
          icon: "mdi-weather-sunset",
          color: "#FF4500",
          date: this.laterToday4pm(),
        },
        {
          key: "tomorrow8am",
          label: "Tomorrow (8 AM)",
          icon: "mdi-weather-sunny",
          color: "#FFD700",
          date: this.dayjs().add(1, "day").set({ hour: 8, minute: 0 }),
        },
      ];
    },
  },
  methods: {
    laterToday4pm() {
      let d = this.dayjs().set({ hour: 16, minute: 0 });
      if (this.dayjs().isSameOrAfter(d)) d = d.add(1, "day");
      return d;
    },
    choosePreset(opt) {
      this.$emit("select", opt.date.toISOString());
    },
    chooseCustom() {
      this.showCustom = false;
      this.$emit("select", this.dayjs(this.customDate).toISOString());
    },
  },
};
</script>

<style scoped>
.quick-reminder-menu {
  padding: 4px 0;
}
</style>
